import { getUserVisibleOrDefaultExceptionMessage, isUserVisibleException } from '@paragon/client-tools';
import {
  DownloadRequest,
  EstimateInfo,
  ShopDrawingOptions,
  TrussDesignDrawingOptions,
} from '@paragon/contract-library';
import {
  designService,
  DownloadableFileResponse,
  EmailResponse,
  NoFileContentResponse,
} from '@paragon/data-connections';
import { getProjectAncestor, getQuantityMap } from '@paragon/layout-models';
import { hasTrePreservePlateTypePermission } from '@paragon/login-tools';
import { addDialog, enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { runWithSpinner } from '@paragon/task-spinner';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import {
  getScopeComponentDesignGuids,
  getScopedComponentDesignQuantities,
  getScopeProjectGuids,
  ProjectActionScope,
} from '../projectActionScope';
import { getDoWorkingChangesExistByLoadedComponentDesignGuid } from '../selectors/getDoWorkingChangesExistByComponentDesignGuid';
import { ChooseTrussDesignDrawingTemplateSubmitAction, QuantitiesDialogSubmitAction } from './dialogActions';
import { createJob } from './projects';
import { AsyncThunkActionReturn, AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

export const continueAfterQuantitiesDialogSubmission =
  (
    quantitiesDialogSubmitAction: QuantitiesDialogSubmitAction,
    componentDesignQuantities: { [guid: string]: number },
    groupName: string,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    let serverRequest: (downloadRequest: DownloadRequest) => Promise<DownloadableFileResponse | NoFileContentResponse>;
    let fileDescriptor: string;
    switch (quantitiesDialogSubmitAction) {
      case 'DownloadCyb':
        serverRequest = designService.componentDesigns.downloadCyb;
        fileDescriptor = 'CYB files';
        break;
      case 'DownloadTres':
        serverRequest = designService.componentDesigns.downloadTre;
        fileDescriptor = 'TRE files';
        break;
      case 'DownloadTps':
        serverRequest = designService.componentDesigns.downloadTps;
        fileDescriptor = 'TPS files';
        break;
      case 'DownloadBlade':
        serverRequest = designService.componentDesigns.downloadBlade;
        fileDescriptor = 'Blade files';
        break;
      case 'DownloadComponentReport':
        serverRequest = designService.componentDesigns.downloadComponentReport;
        fileDescriptor = 'Component Summary';
        break;
      case 'DownloadTrs':
        serverRequest = designService.componentDesigns.downloadTrs;
        fileDescriptor = 'TRS files';
        break;
      case 'DownloadTrussDAndE': {
        // Each file's header fields and eave height are read on the server from the project that
        // component belongs to, so a selection spanning projects sends all of them.
        const state = getState();
        const guidToProjectGuid = Object.fromEntries(
          Object.keys(componentDesignQuantities).map((componentDesignGuid) => [
            componentDesignGuid,
            getProjectAncestor(state, componentDesignGuid).guid,
          ]),
        );
        serverRequest = (downloadRequest) =>
          designService.componentDesigns.downloadTrussDAndE({ ...downloadRequest, guidToProjectGuid });
        fileDescriptor = 'Truss D&E files';
        break;
      }
      case 'DownloadTrsMachinery':
        serverRequest = (downloadRequest) =>
          designService.componentDesigns.downloadMachineryTrs({
            ...downloadRequest,
            memberType: 'All',
            maxMemberLengthInInches: null,
            includeMembersLongerThanMaxLengthInSeparateFile: null,
          });
        fileDescriptor = 'Machinery TRS files';
        break;
      case 'DownloadBvn':
        serverRequest = designService.componentDesigns.downloadBvn;
        fileDescriptor = 'BVN files';
        break;
      case 'DownloadOmn':
        serverRequest = designService.componentDesigns.downloadOmn;
        fileDescriptor = 'OMN files';
        break;
      case 'DownloadLumberPickList':
        serverRequest = designService.componentDesigns.downloadLumberPickList;
        fileDescriptor = 'Lumber Pick List';
        break;
      case 'DownloadPlatesPickList':
        serverRequest = designService.componentDesigns.downloadPlatePickList;
        fileDescriptor = 'Plate Pick List';
        break;
      default:
        assertExhaustiveSwitch(quantitiesDialogSubmitAction, 'Unimplemented QuantitiesDialogSubmitAction');
    }

    if (quantitiesDialogSubmitAction === 'DownloadTres' && !hasTrePreservePlateTypePermission(getState())) {
      dispatch(enqueueWarning('Any TREs without MiTek plates will be converted.'));
    }

    try {
      const fileResult = await dispatch(
        runWithSpinner(
          `Preparing ${fileDescriptor}...`,
          serverRequest({
            guidType: 'ComponentDesign',
            guidToQuantity: componentDesignQuantities,
            groupName,
            sharedComponentSetLookupCode: null,
          }),
        ),
      );

      if (fileResult instanceof NoFileContentResponse) {
        dispatch(enqueueWarning('No file content available for download.'));
      } else {
        fileResult.download();
      }
    } catch (error) {
      dispatch(onDownloadErrorFor(fileDescriptor, error));
    }

    if (quantitiesDialogSubmitAction === 'DownloadTrs') {
      dispatch(enqueueWarning('The download was successful, but note that any bevel information was lost.'));
    }
  };

export const continueAfterChooseTrussDesignDrawingTemplatesSubmission =
  (
    chooseTrussDesignDrawingTemplateSubmitAction: ChooseTrussDesignDrawingTemplateSubmitAction,
    scope: ProjectActionScope,
    selectedTrussDesignDrawingTemplateId: number | null,
    selectedTrussDesignDrawingOverflowTemplateId: number | null,
  ): ThunkActionVoid =>
  (dispatch) => {
    switch (chooseTrussDesignDrawingTemplateSubmitAction) {
      case 'RequestSeals': {
        return dispatch(
          createJob(
            'Seal',
            false,
            scope,
            selectedTrussDesignDrawingTemplateId,
            selectedTrussDesignDrawingOverflowTemplateId,
          ),
        );
      }
      case 'RequestRepair': {
        return dispatch(
          createJob(
            'Repair',
            true,
            scope,
            selectedTrussDesignDrawingTemplateId,
            selectedTrussDesignDrawingOverflowTemplateId,
          ),
        );
      }
      default:
        assertExhaustiveSwitch(
          chooseTrussDesignDrawingTemplateSubmitAction,
          'Unimplemented ChooseTrussDesignDrawingTemplateSubmitAction',
        );
    }
  };

export const downloadMaterialDetails =
  (
    scope: ProjectActionScope,
    componentDesignQuantities: { [guid: string]: number },
    overLengthPrice: number,
    pdf: boolean,
  ): ThunkActionVoid =>
  async (dispatch, getState) => {
    const layoutModels = getState().layoutModels;
    const projects = getScopeProjectGuids(scope)
      .map((projectGuid) => layoutModels[projectGuid])
      .filter(notNullish);
    const projectNames = projects.map((project) => project.name).join(' ');

    if (pdf) {
      try {
        const file = await dispatch(
          runWithSpinner(
            'Downloading Materials Report...',
            designService.componentDesigns.downloadMaterialReportPdf({
              guidType: 'ComponentDesign',
              guidToQuantity: componentDesignQuantities,
              groupName: projectNames,
              sharedComponentSetLookupCode: null,
              overLengthPrice,
            }),
          ),
        );

        file.download();
      } catch (error) {
        dispatch(onDownloadErrorFor('Materials Report', error));
      }
    } else {
      try {
        const file = await dispatch(
          runWithSpinner(
            'Downloading Material Details...',
            designService.componentDesigns.downloadMaterialReportCsv({
              guidType: 'ComponentDesign',
              guidToQuantity: componentDesignQuantities,
              groupName: projectNames,
              sharedComponentSetLookupCode: null,
              overLengthPrice,
            }),
          ),
        );

        file.download();
      } catch (error) {
        dispatch(onDownloadErrorFor('Material Details', error));
      }
    }
  };

export const downloadShopDrawings =
  (
    componentDesignQuantities: { [guid: string]: number },
    options: ShopDrawingOptions,
    groupName: string,
  ): ThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    try {
      const fileResult = await dispatch(
        runWithSpinner(
          'Generating Shop Drawings...',
          designService.componentDesigns.downloadShopDrawings({
            guidType: 'ComponentDesign',
            guidToQuantity: componentDesignQuantities,
            groupName: groupName.trim() || null,
            sharedComponentSetLookupCode: null,
            options,
            allowEmail: true,
          }),
        ),
      );

      if (fileResult instanceof EmailResponse) {
        dispatch(
          addDialog(
            'You will receive an email when the requested' +
              ` download is ready at ${state.logInData.loggedInUser!.email}.`,
          ),
        );
      } else if (fileResult instanceof NoFileContentResponse) {
        dispatch(enqueueWarning('No content available for download.'));
      } else {
        fileResult.download('Shop Drawings.pdf');
      }
    } catch (error) {
      dispatch(onDownloadErrorFor('Shop Drawings', error));
    }
  };

export const exportProject =
  (projectGuid: string): ThunkActionVoid =>
  async (dispatch) => {
    try {
      const fileResult = await dispatch(
        runWithSpinner('Generating Project Zip', designService.projects.exportProject(projectGuid)),
      );

      fileResult.download();
    } catch (error) {
      dispatch(onDownloadErrorFor('Exported Project', error));
    }
  };

export const downloadEngineeringReport =
  (scope: ProjectActionScope): ThunkActionVoid =>
  async (dispatch) => {
    try {
      const fileResult = await dispatch(
        runWithSpinner(
          'Generating Engineering Report...',
          designService.analysisCsvs.postGenerateEngineeringReport(getScopeComponentDesignGuids(scope)),
        ),
      );

      fileResult.download();
    } catch (error) {
      dispatch(onDownloadErrorFor('Engineering Report', error));
    }
  };

export const downloadComprehensiveReports =
  (scope: ProjectActionScope): ThunkActionVoid =>
  async (dispatch) => {
    try {
      const fileResult = await dispatch(
        runWithSpinner(
          'Generating Comprehensive Reports...',
          designService.analysisCsvs.postGenerateComprehensiveReports(getScopeComponentDesignGuids(scope)),
        ),
      );

      fileResult.download();
    } catch (error) {
      dispatch(onDownloadErrorFor('Comprehensive Reports', error));
    }
  };

export const downloadTrussDesignDrawings =
  (options: TrussDesignDrawingOptions, scope: ProjectActionScope): ThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    const projectGuids = getScopeProjectGuids(scope);
    const projects = Object.values(state.layoutModels).filter((model) => projectGuids.includes(model.guid));
    const componentDesignQuantities = getScopedComponentDesignQuantities(scope, getQuantityMap(state), 'fullQuantity');

    const doWorkingChangesExistByComponentDesignGuid = getDoWorkingChangesExistByLoadedComponentDesignGuid(state);
    const someHaveWorkingChanges = Object.entries(componentDesignQuantities).some(
      ([componentDesignGuid, quantity]) =>
        quantity !== 0 && doWorkingChangesExistByComponentDesignGuid[componentDesignGuid] === true,
    );
    if (someHaveWorkingChanges) {
      dispatch(
        enqueueError('Unable to generate TDDs: One or more of these component designs has un-analyzed changes.'),
      );
      return;
    }

    // Each drawing is printed with its own project's name, even when the selection spans projects
    const guidToJobName = Object.fromEntries(
      Object.keys(componentDesignQuantities).map((componentDesignGuid) => [
        componentDesignGuid,
        getProjectAncestor(state, componentDesignGuid).name,
      ]),
    );

    try {
      const fileResult = await dispatch(
        runWithSpinner(
          'Generating Truss Design Drawings...',
          designService.componentDesigns.downloadTrussDesignDrawings({
            ...options,
            guidType: 'ComponentDesign',
            guidToQuantity: componentDesignQuantities,
            guidToJobName,
            groupName: projects.map((project) => project.name).join(' and '),
            sharedComponentSetLookupCode: null,
            // A Shared Component Set can only record the one project it came from
            projectGuid: projectGuids.length === 1 ? projectGuids[0] : null,
          }),
        ),
      );

      fileResult.download();
    } catch (error) {
      if (isUserVisibleException(error)) {
        dispatch(enqueueError(error.userVisibleMessage));
      } else {
        console.error(error);
        dispatch(onDownloadErrorFor('Truss Design Drawings', error));
      }
    }
  };

/**
 * Resolves to whether the estimate was generated (and therefore saved to the project). Still true if only the
 * browser download fails, since the estimate has been saved by then.
 */
function createEstimate(
  fileType: 'PDF' | 'CSV',
  generate: () => Promise<DownloadableFileResponse>,
): AsyncThunkActionReturn<boolean> {
  return async (dispatch) => {
    let fileResult;
    try {
      fileResult = await dispatch(runWithSpinner(`Generating Estimate ${fileType}...`, generate));
    } catch (error) {
      if (isUserVisibleException(error)) {
        dispatch(enqueueError(error.userVisibleMessage));
      } else {
        console.error(error);
        dispatch(onDownloadErrorFor(`Estimate ${fileType}`, error));
      }
      return false;
    }

    try {
      fileResult.download();
    } catch (error) {
      console.error(error);
      dispatch(enqueueError(`The estimate was saved, but its ${fileType} could not be downloaded.`));
    }
    return true;
  };
}

export const createEstimatePdf = (estimateInfo: EstimateInfo, projectGuid: string) =>
  createEstimate('PDF', () => designService.estimates.postGenerateEstimatePdfForProject(estimateInfo, projectGuid));

export const createEstimateCsv = (estimateInfo: EstimateInfo, projectGuid: string) =>
  createEstimate('CSV', () => designService.estimates.postGenerateEstimateCsvForProject(estimateInfo, projectGuid));

export const downloadProjectFiles =
  (projectGuid: string, projectFileGuids: readonly string[]): ThunkActionVoid =>
  async (dispatch) => {
    try {
      const fileResult = await dispatch(
        runWithSpinner(
          'Downloading project files...',
          designService.projectFiles.download(projectGuid, projectFileGuids),
        ),
      );

      fileResult.download();
    } catch (error) {
      if (isUserVisibleException(error)) {
        dispatch(enqueueError(error.userVisibleMessage));
      } else {
        console.error(error);
        dispatch(enqueueError(getUserVisibleOrDefaultExceptionMessage(error, 'Error downloading project files')));
      }
    }
  };

const onDownloadErrorFor =
  (fileType: string, error: unknown): ThunkActionVoid =>
  (dispatch) => {
    console.error(error);
    dispatch(
      enqueueError(
        getUserVisibleOrDefaultExceptionMessage(
          error,
          `Error downloading ${fileType}. Please try again or contact Paragon Customer Support.`,
        ),
      ),
    );
  };
